import { useCallback, useEffect, useState, MouseEvent, useRef } from 'react';
import { Palette, PaletteColor, Theme, useTheme } from '@mui/material/styles';
import { format } from 'date-fns';

import { SelectOption } from 'components/SelectBase';

import moment from 'moment';
import 'moment-precise-range-plugin';
import { DATE_FORMAT } from 'const';

export type SubType<Base, Condition> = Pick<
  Base,
  {
    [Key in keyof Base]: Base[Key] extends Condition ? Key : never;
  }[keyof Base]
>;

export type Color = keyof SubType<Palette, PaletteColor>;

export function themeColor(theme: Theme, color: Color) {
  const palette = theme.palette;
  const c = palette[color];

  return palette.mode === 'light' ? c.light : c.dark;
}

type OptionConfig = {
  label: string;
  color?: Color;
};

export type Options<T extends string | number> = Record<T, OptionConfig>;

export function makeOption<T extends string | number>(
  key: T,
  config: Options<T>,
  theme?: Theme
) {
  const c = config[key];

  const result: SelectOption<T> = { ...c, value: key };

  if (theme && c.color) {
    result.color = themeColor(theme, c.color);
  }

  return result;
}

export function useOption<T extends string | number>(
  key: T,
  config: Options<T>
) {
  const theme = useTheme();

  return makeOption(key, config, theme);
}

export function useOnLoad(load: () => void) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (loaded) return;
    load();

    setLoaded(true);
  }, [load, loaded]);
}

export function useSkipFirstLoad(callback: () => any) {
  const firstLoad = useRef(true);

  useEffect(() => {
    const skip = firstLoad.current;
    firstLoad.current = false;

    if (skip) return;

    return callback();
  }, [callback]);
}

let debounceTimer: NodeJS.Timeout;

export function debounce(callback: () => void, time: number) {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(callback, time);
}

// FIXME: @Cleanup can we move this to the backend
export function duration(start: string, end?: string) {
  const { days, hours, minutes } = moment(start).preciseDiff(moment(end), true);

  let string = '';
  if (days) {
    string += `${days} d. `;
  }
  if (hours) {
    string += `${hours} val. `;
  }
  if (minutes) {
    string += `${minutes} min. `;
  }

  return string;
}

// FIXME: @Cleanup can we move this to the backend
export function durationFromString(duration: string) {
  const range = moment.duration(duration);

  const days = range.days();
  const hours = range.hours();
  const minutes = range.minutes();

  let string = '';
  if (days) {
    string += `${days} d. `;
  }
  if (hours) {
    string += `${hours} val. `;
  }
  if (minutes) {
    string += `${minutes} min. `;
  }

  return string;
}

export function useHoverable() {
  const [hovering, setHovering] = useState(false);

  const onMouseEnter = useCallback(() => {
    setHovering(true);
  }, []);

  const onMouseLeave = useCallback(() => {
    setHovering(false);
  }, []);

  return { hovering, onMouseEnter, onMouseLeave };
}

export type UseConfirmProps<T extends HTMLElement = HTMLElement> = {
  enabled?: boolean;
  timeout?: number;
  onConfirm?: (event: MouseEvent<T>) => void;
};

type Timeout = ReturnType<typeof setTimeout>;

export function useConfirm<T extends HTMLElement = HTMLElement>({
  enabled = true,
  timeout = 2000,
  onConfirm,
}: UseConfirmProps) {
  const [confirming, setConfirming] = useState(false);
  const ref = useRef<Timeout>();

  const onClick = useCallback(
    (event: MouseEvent<T>) => {
      event.stopPropagation();

      if (!enabled || confirming) {
        onConfirm && onConfirm(event);
      } else {
        setConfirming(true);

        ref.current = setTimeout(() => {
          setConfirming(false);
        }, timeout);
      }
    },
    [onConfirm, enabled, confirming, timeout]
  );

  useEffect(
    () => () => {
      if (ref.current) {
        clearTimeout(ref.current);
      }
    },
    []
  );

  return {
    confirming: enabled && confirming,
    onClick,
  };
}

export const textFieldValue = (number: number | null | undefined) =>
  number?.toString() ?? '';

export const dateFieldValue = (dateString: string | null) =>
  dateString ? new Date(dateString) : null;

export const dateString = (date: Date | null) =>
  date ? format(date, DATE_FORMAT) : null;

export const stringToNumber = (value: string) =>
  value.length > 0 ? Number(value) : null;

type Entity = {
  id: string;
};

type EntityField = Entity | null;

export function setEntityField<T extends EntityField>(value: T) {
  return value
    ? {
        id: value.id,
      }
    : null;
}

export function humanFileSize(size: number) {
  const i = size === 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024));

  return (
    (size / Math.pow(1024, i)).toFixed(2) +
    ' ' +
    ['B', 'kB', 'MB', 'GB', 'TB'][i]
  );
}
